import { inject, Injectable } from '@angular/core';
import equal from 'fast-deep-equal';

import { SchemaService } from '@blaze-shared/schema-data-access';
import { type ExpandWorkflowNode } from '@blaze-shared/workflow-domain';

import { normalizeNodeInput } from '../../node-input-rows';

/**
 * Turns a nested column into the node's output rows.
 *
 * What the column holds decides the result: a list of objects expands into those objects, a single
 * object into that object, and primitives into one row each keyed by the column's own name.
 * Duplicates are dropped, and a row that lacks the column contributes nothing.
 */
@Injectable({ providedIn: 'root' })
export class ExpandActionService {
  private readonly schemaService = inject(SchemaService);

  expand(node: ExpandWorkflowNode, nodeInput: any): any[] {
    const columnName = this.schemaService.getColumnName(node.column);
    if (!nodeInput || !columnName) {
      return [];
    }

    let expanded: any[] = [];
    let hasPrimitiveValues = false;

    normalizeNodeInput(nodeInput).forEach((row: any) => {
      if (!Object.prototype.hasOwnProperty.call(row, columnName)) {
        return;
      }
      const values = row[columnName];

      if (Array.isArray(values) && typeof values?.[0] === 'object') {
        values.forEach((value) => pushUnique(expanded, value, value));
      } else if (Array.isArray(values)) {
        hasPrimitiveValues = true;
        expanded = expanded.concat(values.map((value) => ({ [columnName]: value })));
      } else if (values && typeof values === 'object') {
        pushUnique(expanded, values, values);
      } else if (values) {
        hasPrimitiveValues = true;
        pushUnique(expanded, values, { [columnName]: values });
      }
    });

    // Rows built from primitives can repeat across input rows, so dedupe them by value.
    return hasPrimitiveValues ? dedupeBySerialization(expanded) : expanded;
  }
}

/** Appends `row` unless something deep-equal to `value` is already there. */
function pushUnique(expanded: any[], value: unknown, row: unknown): void {
  if (!expanded.some((existing) => equal(existing, value))) {
    expanded.push(row);
  }
}

function dedupeBySerialization(expanded: any[]): any[] {
  const serialized = expanded.filter((value) => !!value).map((value) => JSON.stringify(value));
  return Array.from(new Set(serialized))
    .filter((uniqueValue) => !!uniqueValue)
    .map((serializedValue) => JSON.parse(serializedValue));
}
